import React, { FC, useEffect, useMemo, useState } from "react";
import { usePageStore } from "@/hooks/usePageStore";
import { Box, CardContent, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useAppTheme } from "@/utils/theme";
import Card from "@mui/material/Card";
import DataTable, { createDataRow, HeadCell } from "@/components/DataTable";
import { SkeletonHeaderButtons } from "@/ui/header/skeleton-button";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import AddIcon from "@mui/icons-material/Add";
import Chip from "@mui/material/Chip";
import { AlertDialog } from "@/components/AlertDialog/AlertDialog";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useCreateAuditlog } from "@/hooks/useAuditLogs";
import { useUserStore } from "@/hooks/useUserStore";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import LightTooltip from "@/components/LightTooltip";
import SearchBox from "@/components/SearchBox";
import AuditLogModal from "@/ui/modals/AuditLogModal";
import useDebounce from "@/hooks/useDebounce";
import { Filters } from "@/ui/content/users/Filters";
import { useUserDeleteOne, useUsers } from "@/hooks/useUsers";
import { UserStatusEnum } from "@/domain/user.enum";
import Select from "@/components/Select";
import { useSearchRoles } from "@/hooks/management/useRoles.manage";
import { BackOfficeUser } from "@/domain/user.interface";
import { PermissionDeniedModal } from "@/components/PermissionDeniedModal/PermissionDeniedModal";
import { UserModal } from "@/ui/modals/users/UserModal";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";

const UsersPage: FC = () => {
  const theme = useAppTheme();
  const { setPageTitle } = usePageStore();
  const { t } = useTranslation("user");
  const { t: tCommon } = useTranslation("common");

  const { showSnackBar } = useFeedBack();
  const {
    userInfo,
    userHasAccessToCode,
    roles: loggedUserRoles,
  } = useUserStore();

  const [showPermissionDeniedModal, setShowPermissionDeniedModal] =
    useState(false);

  const createAuditLog = useCreateAuditlog();
  const userDeleteOne = useUserDeleteOne();

  const auditLogViewCalledRef = React.useRef(false);

  useEffect(() => {
    setPageTitle(t("title"));
  }, []);

  const statusOptions = useMemo(() => {
    return Object.values(UserStatusEnum).reduce(
      (acc, status) => {
        acc[status] = tCommon(`enums.${status}`);
        return acc;
      },
      {} as Record<string, string>,
    );
  }, []);
  const { rolesResponse, isLoadingRoles } = useSearchRoles();

  // Filter and categorize roles based on logged user's permissions (similar to UserModal.container.tsx)
  const categorizedRoles = useMemo(() => {
    const allRoles = rolesResponse?.roles || [];
    const standardSystemRoles: typeof allRoles = [];
    const standardAccountRoles: typeof allRoles = [];
    const customRoles: typeof allRoles = [];

    // Get logged user's role names
    const loggedUserRoleNames =
      loggedUserRoles?.map((r) => r.name?.toLowerCase() || "") || [];

    // Check if user has System Admin, Account Admin, or Support role
    const isSystemAdmin = loggedUserRoleNames.some(
      (name) => name === "system admin" || name.includes("system admin"),
    );
    const isAccountAdmin = loggedUserRoleNames.some(
      (name) => name === "account admin" || name.includes("account admin"),
    );
    const isSupport = loggedUserRoleNames.some(
      (name) => name === "support" || name.includes("support"),
    );

    allRoles.forEach((role) => {
      const roleType = role.type as string;
      const roleName = (role.name || "").toLowerCase();

      // Match COYN types: STANDARD_SYSTEM_ROLE, STANDARD_ACCOUNT_ROLE, CUSTOM_ROLE
      if (roleType === "STANDARD_SYSTEM_ROLE") {
        // Filter standard system roles based on logged user
        if (isSystemAdmin) {
          // System Admin sees all: Support, Account Admin, System Admin
          standardSystemRoles.push(role);
        } else if (isAccountAdmin || isSupport) {
          // Account Admin or Support see only Account Admin
          if (
            roleName === "account admin" ||
            roleName.includes("account admin")
          ) {
            standardSystemRoles.push(role);
          }
        }
        // Other roles don't see standard system roles
      } else if (roleType === "STANDARD_ACCOUNT_ROLE") {
        // Standard account roles - always visible
        standardAccountRoles.push(role);
      } else if (roleType === "CUSTOM_ROLE") {
        customRoles.push(role);
      }
    });

    return {
      standardSystemRoles: standardSystemRoles.sort((a, b) =>
        (a.name || "").localeCompare(b.name || ""),
      ),
      standardAccountRoles: standardAccountRoles.sort((a, b) =>
        (a.name || "").localeCompare(b.name || ""),
      ),
      customRoles: customRoles.sort((a, b) =>
        (a.name || "").localeCompare(b.name || ""),
      ),
    };
  }, [rolesResponse?.roles, loggedUserRoles]);

  // AC02, AC03: Role options with "All roles" as first option - filtered based on logged user permissions
  const rolesOptions = useMemo(() => {
    const options = [{ key: "all", value: "All roles" }];

    // Combine all filtered roles
    const allFilteredRoles = [
      ...categorizedRoles.standardSystemRoles,
      ...categorizedRoles.standardAccountRoles,
      ...categorizedRoles.customRoles,
    ];

    // Sort alphabetically
    const sortedRoles = allFilteredRoles.sort((a, b) =>
      (a.name || "").localeCompare(b.name || ""),
    );

    sortedRoles.forEach((role) => {
      if (role.name) {
        options.push({ key: role._id, value: role.name });
      }
    });

    return options;
  }, [categorizedRoles]);

  // Create a map of roleId -> role name for display
  const roleIdToNameMap = useMemo(() => {
    const map: Record<string, string> = {};
    if (rolesResponse?.roles) {
      rolesResponse.roles.forEach((role) => {
        if (role._id && role.name) {
          map[role._id] = role.name;
        }
      });
    }
    return map;
  }, [rolesResponse?.roles]);

  // Create a set of available role IDs that the logged user can see
  const availableRoleIds = useMemo(() => {
    const roleIds = new Set<string>();
    [
      ...categorizedRoles.standardSystemRoles,
      ...categorizedRoles.standardAccountRoles,
      ...categorizedRoles.customRoles,
    ].forEach((role) => {
      if (role._id) {
        roleIds.add(role._id);
      }
    });
    return roleIds;
  }, [categorizedRoles]);

  const [showDeleteModal, setShowDeleteModal] = useState<{
    open: boolean;
    user: BackOfficeUser | null;
  }>({
    open: false,
    user: null,
  });

  const [openAuditLogModal, setOpenAuditLogModal] = useState(false);
  const [isUserModalOpen, setIsUserModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<"create" | "edit">("create");
  const [selectedUserId, setSelectedUserId] = useState<string | null>(null);

  // AC00-B: Check permission before opening audit log modal (COYN-908: use PermissionDeniedModal)
  const handleOpenAuditLog = () => {
    if (!userHasAccessToCode("USERS_AUDIT_LOG_VIEW")) {
      setShowPermissionDeniedModal(true);
      return;
    }
    setOpenAuditLogModal(true);
  };

  const filtersState = useState<Filters>({
    sortField: "firstName",
    sortOrder: 1,
    roleId: "all", // AC03: Default to "All roles"
    orgUnitIds: [],
    search: "",
    pageNumber: 0,
    pageSize: 10,
    status: [UserStatusEnum.PENDING, UserStatusEnum.ACTIVE], // AC04: Default: PENDING and ACTIVE
  });

  const [filters, setFilters] = filtersState;
  const searchDebounce = useDebounce({
    value: filters.search,
    delay: 500,
  });

  // Determine which role IDs to pass to the API based on filter selection and available roles
  const roleIdsForApi = useMemo(() => {
    if (filters.roleId === "all") {
      // When "All roles" is selected, pass all available role IDs to the API
      // This ensures users only see users with roles they have permission to view
      return Array.from(availableRoleIds);
    } else {
      // When a specific role is selected, pass only that role ID
      return [filters.roleId];
    }
  }, [filters.roleId, availableRoleIds]);

  const { users, isLoadingUsers, refetchUsers } = useUsers({
    sortField: filters.sortField,
    sortOrder: filters.sortOrder,
    pageNumber: filters.pageNumber,
    pageSize: filters.pageSize,
    search: filters.search,
    orgUnitId: filters.orgUnitIds,
    roleId: roleIdsForApi,
    status: filters.status,
  });

  // Get role name from roleIds (first role if multiple)
  const getUserRoleName = (user: BackOfficeUser): string => {
    if (!user.roleIds || user.roleIds.length === 0) return "-";
    const firstRoleId = user.roleIds[0];
    return roleIdToNameMap[firstRoleId] || "-";
  };

  const rows = useMemo(() => {
    if (!users || users.users.length === 0) return [];

    return users.users.map((user, index) => {
      // AC07: Map user data to table format
      // Column order: fullName, role, status, action
      const row = createDataRow(user._id || index, {
        fullName: (
          <Typography variant="bodySmall">
            {user.firstName} {user.lastName}
          </Typography>
        ),
        role: (
          <Typography variant="bodyMedium">{getUserRoleName(user)}</Typography>
        ),
        status: (
          <Chip
            sx={{
              bgcolor:
                user.status === UserStatusEnum.ACTIVE
                  ? theme.palette.brand.light
                  : user.status === UserStatusEnum.PENDING
                    ? theme.palette.warning.light
                    : theme.palette.grey[300],
              borderWidth: 1,
              borderStyle: "solid",
              borderColor:
                user.status === UserStatusEnum.ACTIVE
                  ? theme.palette.brand.main
                  : user.status === UserStatusEnum.PENDING
                    ? theme.palette.warning.dark
                    : theme.palette.grey[500],
            }}
            label={
              <Typography variant="labelSmall">
                {statusOptions[user.status] || user.status}
              </Typography>
            }
            size="medium"
          />
        ),
        action: (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
            }}
          >
            <ChevronRightIcon
              sx={{
                color: theme.palette.icon?.main || "text.secondary",
                fontSize: "1.25rem",
              }}
            />
          </Box>
        ),
      });

      return row;
    });
  }, [users, statusOptions, roleIdToNameMap, theme]);

  // AC00-C: Define table columns per Field Spec
  // User (sortable), Role (sortable, hidden on mobile), Status (sortable, hidden on mobile), Action (chevron)
  const headCells: HeadCell[] = [
    {
      id: "fullName",
      isSortable: true,
      disablePadding: false,
      label: "User",
      align: "left",
    },
    {
      id: "role",
      isSortable: true,
      disablePadding: false,
      label: "Role",
      align: "left",
    },
    {
      id: "status",
      isSortable: true,
      disablePadding: false,
      label: "Status",
      align: "left",
    },
    {
      id: "action",
      isSortable: false,
      disablePadding: false,
      label: "",
      align: "right",
    },
  ];

  const handleDelete = async () => {
    await userDeleteOne.mutateAsync(
      { userId: showDeleteModal.user?._id as string },
      {
        onSuccess: () => {
          createAuditLog.mutate({
            appType: "WEB_BACK_OFFICE",
            module: MODULE_NAME.USERS,
            option: OPTIONS.DELETE,
            actionCode: ACTION_CODE.WEB_USRS_USR_DELETE,
            action: "When deleting a user",
            detail: `Delete the user ${showDeleteModal.user?.email}`,
            transactionDate: new Date(),
            accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
            createdBy: userInfo?._id as string,
            name: `${userInfo?.firstName} ${userInfo?.lastName}`,
          });

          refetchUsers();
          setShowDeleteModal({ open: false, user: null });
          showSnackBar(t("messages.delete-success"), "success");
        },
      },
    );
  };

  useEffect(() => {
    if (userInfo && !auditLogViewCalledRef.current) {
      createAuditLog.mutate({
        appType: "WEB_BACK_OFFICE",
        module: MODULE_NAME.USERS,
        option: OPTIONS.USERS_LIST,
        actionCode: ACTION_CODE.WEB_USRS_LIST_VIEW,
        action: "When entering",
        detail: "Viewed the list of users",
        transactionDate: new Date(),
        accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
        createdBy: userInfo?._id as string,
        name: `${userInfo?.firstName} ${userInfo?.lastName}`,
      });
      auditLogViewCalledRef.current = true;
    }
  }, [userInfo]);

  // AC00-A: Log WEB_USRS_LIST_SEARCH when search filter changes
  const previousSearchRef = React.useRef<string>("");
  useEffect(() => {
    if (userInfo) {
      // Only log if search term has changed and is not empty
      if (
        searchDebounce !== previousSearchRef.current &&
        searchDebounce &&
        searchDebounce.trim().length > 0
      ) {
        previousSearchRef.current = searchDebounce;
        createAuditLog.mutate({
          appType: "WEB_BACK_OFFICE",
          module: MODULE_NAME.USERS,
          option: OPTIONS.USERS_LIST,
          actionCode: ACTION_CODE.WEB_USRS_LIST_SEARCH,
          action: "When searching",
          detail: "Searched within the list of users",
          transactionDate: new Date(),
          accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
          createdBy: userInfo?._id as string,
          name: `${userInfo?.firstName} ${userInfo?.lastName}`,
        });
      }
    }
  }, [userInfo, searchDebounce, createAuditLog]);

  return (
    <Box>
      {/* Top Bar - Header with title and buttons */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          mt: 0,
        }}
      >
        <Typography variant="headlineMedium">Users</Typography>
        <Box sx={{ display: "flex", gap: 1 }}>
          {isLoadingUsers ? (
            <SkeletonHeaderButtons />
          ) : (
            <>
              <LightTooltip title={t("buttons.audit-log")} arrow theme={theme}>
                <IconButton
                  size="small"
                  sx={{
                    border: "1px solid",
                    borderColor: theme.palette.primary.main,
                    borderRadius: 1,
                    "&:hover": {
                      borderColor: theme.palette.primary.light,
                      backgroundColor: "rgba(98, 83, 162, 0.08)",
                    },
                  }}
                  onClick={handleOpenAuditLog}
                >
                  <HistoryOutlinedIcon
                    sx={{ color: theme.palette.primary.main }}
                  />
                </IconButton>
              </LightTooltip>
              <Button
                variant="contained"
                color="primary"
                startIcon={<AddIcon />}
                onClick={() => {
                  if (!userHasAccessToCode("USER_ADD")) {
                    setShowPermissionDeniedModal(true);
                    return;
                  }
                  setSelectedUserId(null);
                  setModalMode("create");
                  setIsUserModalOpen(true);
                }}
              >
                ADD
              </Button>
            </>
          )}
        </Box>
      </Box>

      <Card>
        <CardContent>
          {/* Filters - Search on left, dropdowns on right */}
          <Box
            sx={{
              display: "flex",
              gap: 1, // 10px gap (1 * 10px spacing unit)
              mb: 2,
              flexDirection: { xs: "column", md: "row" },
            }}
          >
            {
            <Box
              sx={{
                flex: { xs: "none", md: "0 0 50%" }, // 1/2 width
                width: { xs: "100%", md: "auto" },
              }}
            >
              <SearchBox
                theme={theme}
                onChange={(value) => {
                  setFilters((filters) => ({
                    ...filters,
                    search: value,
                    pageNumber: 0,
                  }));
                }}
                value={filters.search}
                placeHolder="Search..."
              />
            </Box>
            }
            <Box
              sx={{
                flex: { xs: "none", md: "0 0 calc(25% - 5px)" }, // 1/4 width minus gap
                width: { xs: "100%", md: "auto" },
              }}
            >
              <Select
                handleChange={(value) => {
                  // AC02, AC03: Handle role filter change
                  setFilters((filters) => ({
                    ...filters,
                    roleId: value as string,
                    pageNumber: 0,
                  }));
                }}
                data={rolesOptions || []}
                value={filters.roleId || "all"}
                label="Roles"
                id="users-roles"
                isLoading={isLoadingRoles}
                width="100%"
              />
            </Box>
            <Box
              sx={{
                flex: { xs: "none", md: "0 0 calc(25% - 5px)" }, // 1/4 width minus gap
                width: { xs: "100%", md: "auto" },
              }}
            >
              <Select
                handleChange={(value) => {
                  // AC04: Handle status filter change (multi-select)
                  setFilters((filters) => ({
                    ...filters,
                    status: Array.isArray(value) ? value : [value],
                    pageNumber: 0,
                  }));
                }}
                data={[
                  {
                    key: UserStatusEnum.PENDING,
                    value: tCommon(`enums.${UserStatusEnum.PENDING}`),
                  },
                  {
                    key: UserStatusEnum.ACTIVE,
                    value: tCommon(`enums.${UserStatusEnum.ACTIVE}`),
                  },
                  {
                    key: UserStatusEnum.INACTIVE,
                    value: tCommon(`enums.${UserStatusEnum.INACTIVE}`),
                  },
                ]}
                value={filters.status || []}
                label="Status"
                id="users-status"
                width="100%"
                multiple
              />
            </Box>
          </Box>
          <DataTable
            data={rows}
            headCells={headCells}
            order={filters.sortOrder === 1 ? "asc" : "desc"}
            orderBy={filters.sortField}
            rowsPerPage={filters.pageSize}
            page={filters.pageNumber}
            total={users?.totalCount}
            isLoading={isLoadingUsers}
            onChangePage={(event, newPage) => {
              setFilters((filters) => ({
                ...filters,
                pageNumber: newPage,
              }));
            }}
            onChangeSize={(event) => {
              setFilters((filters) => ({
                ...filters,
                pageNumber: 0,
                pageSize: Number(event.target.value),
              }));
            }}
            onChangeSort={(order, orderBy) => {
              // Map table column id to backend field name
              let backendField = orderBy;
              if (orderBy === "fullName") {
                backendField = "firstName"; // Sort by firstName for full name
              } else if (orderBy === "role") {
                backendField = "firstName"; // Fallback to firstName for role sorting
              }

              setFilters((filters) => ({
                ...filters,
                pageNumber: 0,
                sortField: backendField as string,
                sortOrder: order === "asc" ? 1 : -1,
              }));
            }}
            onRowClick={(event, id) => {
              // AC07: Open edit modal when clicking on a row (COYN-908: use PermissionDeniedModal)
              if (!userHasAccessToCode("USER_EDIT")) {
                setShowPermissionDeniedModal(true);
                return;
              }
              const userId = String(id);
              setSelectedUserId(userId);
              setModalMode("edit");
              setIsUserModalOpen(true);
            }}
            rowSx={() => ({
              "&:hover": {
                backgroundColor: "#F4F4F9", // Per Field Spec
              },
              cursor: "pointer",
            })}
            messageEmptyData="No items to show"
          />
        </CardContent>
      </Card>

      {showDeleteModal.open && (
        <AlertDialog
          open={showDeleteModal.open}
          title={t("messages.delete-title")}
          description={t("messages.delete-confirm")}
          onCancel={() => {
            setShowDeleteModal({ open: false, user: null });
          }}
          onConfirm={handleDelete}
          confirmText={t("buttons.confirm")}
          cancelText={t("buttons.cancel")}
        />
      )}

      {openAuditLogModal ? (
        <AuditLogModal
          open={openAuditLogModal}
          module={MODULE_NAME.USERS}
          onClose={() => setOpenAuditLogModal(false)}
        />
      ) : null}

      <PermissionDeniedModal
        open={showPermissionDeniedModal}
        onClose={() => setShowPermissionDeniedModal(false)}
      />

      {/* User Modal */}
      <UserModal
        open={isUserModalOpen}
        mode={modalMode}
        userId={selectedUserId || undefined}
        onClose={() => {
          setIsUserModalOpen(false);
          setSelectedUserId(null);
        }}
        onSuccess={() => {
          refetchUsers();
        }}
        canAddUser={userHasAccessToCode("USER_ADD")}
        canEditUser={userHasAccessToCode("USER_EDIT")}
        canDeleteUser={userHasAccessToCode("USER_DELETE")}
        canResendInvitation={userHasAccessToCode("USER_RESEND_INVITATION")}
      />
    </Box>
  );
};

export default UsersPage;
